/**
 * Victory (§1.9): elimination (0 hexes, or < 8 hexes and no cities, surrender, or — from
 * 10:00 — a lost capital, §1.11), last player / team standing, domination (≥ 65% area for
 * 60 s), time.
 */
import { BALANCE } from '../balance.js';
import { capEff, capitalsExposed, isAlive, isStructure, removeEntity } from '../entities.js';
import type { EndReason, MatchResult, MatchState, PlayerId, PlayerState, SimEvent } from '../types.js';
import { clearAlliances } from './alliances.js';
import { refreshAggregates, teamScore } from './scoring.js';

const V = BALANCE.victory;

export function victorySystem(state: MatchState, events: SimEvent[]): void {
  let eliminated = false;
  let lastSurrender = false;
  const exposed = capitalsExposed(state);
  for (const p of state.players) {
    if (!isAlive(p)) continue;
    const decapitated = exposed && capitalLost(state, p);
    if (decapitated || p.surrendered || p.hexCount === 0 || (p.hexCount < V.elimMinHexes && p.cityCount === 0)) {
      eliminate(state, p.id, events, decapitated);
      eliminated = true;
      lastSurrender = p.surrendered;
    }
  }
  if (eliminated) refreshAggregates(state);

  const teams = state.settings.mode === 'teams';
  const groupOf = (id: PlayerId) => (teams ? state.players[id - 1].team : id);
  const allGroups = new Set(state.players.map((p) => groupOf(p.id)));
  const aliveGroups = new Set(state.players.filter(isAlive).map((p) => groupOf(p.id)));

  if (allGroups.size >= 2 && aliveGroups.size <= 1) {
    const g = [...aliveGroups][0];
    end(state, lastSurrender ? 'surrender' : 'elimination', g === undefined ? [] : membersOf(state, g, teams), events);
    return;
  }

  // domination
  const share = new Map<number, number>();
  for (const p of state.players) if (isAlive(p)) share.set(groupOf(p.id), (share.get(groupOf(p.id)) ?? 0) + p.areaShare);
  let holder = 0;
  for (const [g, s] of [...share].sort((a, b) => a[0] - b[0])) if (s >= V.dominationShare) holder = g;
  if (holder !== state.domination.holder) state.domination = { holder, sinceTick: state.tick };
  else if (holder && state.tick - state.domination.sinceTick >= V.dominationTicks) {
    end(state, 'domination', membersOf(state, holder, teams), events);
    return;
  }

  if (state.tick >= state.matchTicks) {
    let best = -1;
    let winners: number[] = [];
    for (const g of [...aliveGroups].sort((a, b) => a - b)) {
      const s = teams ? teamScore(state, g) : state.players[g - 1].score;
      if (s > best) {
        best = s;
        winners = [g];
      } else if (s === best) winners.push(g);
    }
    end(state, 'time', winners.flatMap((g) => membersOf(state, g, teams)), events);
  }
}

/** The player's capital is ruined (no city on its hex) or its hex belongs to someone else. */
export function capitalLost(state: MatchState, p: PlayerState): boolean {
  const h = p.capitalHex;
  if (state.owner[h] !== p.id) return true;
  const sid = state.structId[h];
  return sid < 0 || state.entities.get(sid)?.kind !== 'city';
}

function membersOf(state: MatchState, group: number, teams: boolean): PlayerId[] {
  return teams ? state.players.filter((p) => p.team === group).map((p) => p.id) : [group];
}

function end(state: MatchState, reason: EndReason, winners: PlayerId[], events: SimEvent[]): void {
  const result: MatchResult = {
    reason,
    winners,
    tick: state.tick,
    standings: [...state.players]
      .sort((a, b) => b.score - a.score || a.id - b.id)
      .map((p) => ({
        playerId: p.id,
        score: p.score,
        areaShare: p.areaShare,
        popShare: p.popShare,
        killShare: p.killShare,
        ...(p.eliminatedTick >= 0 ? { eliminatedTick: p.eliminatedTick } : {}),
      })),
  };
  state.result = result;
  events.push({ type: 'end', result });
}

/**
 * Player out: land goes neutral, cities become neutral prizes, everything else (but warheads
 * in flight) dies. The player keeps receiving snapshots as a spectator (vision system).
 */
export function eliminate(state: MatchState, id: PlayerId, events: SimEvent[], decapitated = false): void {
  const p = state.players[id - 1];
  p.eliminatedTick = state.tick;
  p.focusHex = -1;
  state.stats.players[id - 1].eliminatedTick = state.tick;
  state.stats.players[id - 1].decapitated = decapitated;
  for (let h = 0; h < state.world.landCount; h++) {
    if (state.owner[h] !== id) continue;
    state.owner[h] = 0;
    state.prog[h] = 0;
    state.progBy[h] = 0;
    state.pop[h] = BALANCE.pop.neutralFrac * capEff(state, h);
  }
  const doomed: number[] = [];
  for (const e of state.entities.values()) {
    if (e.owner !== id || e.kind === 'icbm' || e.kind === 'slbm' || e.kind === 'bomb') continue;
    if (e.kind === 'city' && isStructure(e) && e.buildDoneTick <= state.tick) {
      e.owner = 0;
      e.upgradeDoneTick = 0;
      e.upgradeCost = 0;
    } else doomed.push(e.id);
  }
  for (const eid of doomed) removeEntity(state, eid, 'destroyed', events);
  clearAlliances(state, id, events);
  state.sensorsDirty |= 1 << id;
  events.push({ type: 'eliminated', player: id, surrendered: p.surrendered });
  events.push({ type: 'phase', phase: { kind: 'eliminated', a: id, b: decapitated ? 1 : 0, tick: state.tick } });
}
